import { useState, useEffect, createContext, useContext } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { 
  Smile, Calendar as CalendarIcon, Clock, User, LogOut, Plus, 
  ChevronLeft, ChevronRight, Check, X, Phone, Mail, Home
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Patient Portal Auth Context
const PortalAuthContext = createContext(null);

const usePortalAuth = () => useContext(PortalAuthContext);

// Main Patient Portal Component
const PatientPortal = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [patient, setPatient] = useState(null);
  const [token, setToken] = useState(null);
  const [view, setView] = useState('login'); // login, register, dashboard, book

  useEffect(() => {
    // Check for existing session
    const savedToken = localStorage.getItem('portal_token');
    const savedPatient = localStorage.getItem('portal_patient');
    if (savedToken && savedPatient) {
      setToken(savedToken);
      setPatient(JSON.parse(savedPatient));
      setIsAuthenticated(true);
      setView('dashboard');
    }
  }, []);

  const handleLogin = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/portal/login`, { email, password });
      const { access_token, patient: patientData } = response.data;
      
      localStorage.setItem('portal_token', access_token);
      localStorage.setItem('portal_patient', JSON.stringify(patientData));
      
      setToken(access_token);
      setPatient(patientData);
      setIsAuthenticated(true);
      setView('dashboard');
      toast.success('Welcome back!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    }
  };

  const handleRegister = async (data) => {
    try {
      await axios.post(`${API_URL}/api/portal/register`, data);
      toast.success('Registration successful! Please login.');
      setView('login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_patient');
    setToken(null);
    setPatient(null);
    setIsAuthenticated(false);
    setView('login');
  };

  const contextValue = { token, patient, handleLogout };

  return (
    <PortalAuthContext.Provider value={contextValue}>
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-100" data-testid="patient-portal">
        <Toaster position="top-center" />
        
        {/* Header */}
        <header className="bg-white shadow-sm">
          <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-teal-600 flex items-center justify-center">
                <Smile className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-slate-800">MyMedPal Dental</h1>
                <p className="text-xs text-slate-500">Patient Portal</p>
              </div>
            </div>
            {isAuthenticated && (
              <div className="flex items-center gap-4">
                <span className="text-sm text-slate-600">
                  Welcome, {patient?.first_name}
                </span>
                <Button variant="outline" size="sm" onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </div>
            )}
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-6xl mx-auto px-4 py-8">
          {!isAuthenticated ? (
            view === 'login' ? (
              <LoginForm onLogin={handleLogin} onSwitchToRegister={() => setView('register')} />
            ) : (
              <RegisterForm onRegister={handleRegister} onSwitchToLogin={() => setView('login')} />
            )
          ) : (
            view === 'book' ? (
              <BookAppointment onBack={() => setView('dashboard')} />
            ) : (
              <Dashboard onBook={() => setView('book')} />
            )
          )}
        </main>

        {/* Footer */}
        <footer className="fixed bottom-0 w-full bg-white border-t py-3 text-center text-xs text-slate-500">
          MyMedPal Dental Patient Portal | Need help? Call (876) 555-0123
        </footer>
      </div>
    </PortalAuthContext.Provider>
  );
};

// Login Form
const LoginForm = ({ onLogin, onSwitchToRegister }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await onLogin(email, password);
    setLoading(false);
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-teal-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800">Patient Login</h2>
          <p className="text-slate-500 mt-2">Access your appointments and book new visits</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
            />
          </div>
          <div>
            <Label>Password</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
          </div>
          <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-slate-500">
            New patient?{' '}
            <button onClick={onSwitchToRegister} className="text-teal-600 font-medium hover:underline">
              Create an account
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

// Register Form
const RegisterForm = ({ onRegister, onSwitchToLogin }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
    date_of_birth: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    await onRegister({
      email: formData.email,
      password: formData.password,
      first_name: formData.first_name,
      last_name: formData.last_name,
      phone: formData.phone,
      date_of_birth: formData.date_of_birth
    });
    setLoading(false);
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-slate-800">Create Account</h2>
          <p className="text-slate-500 mt-2">Register to book appointments online</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>First Name</Label>
              <Input
                value={formData.first_name}
                onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                required
              />
            </div>
            <div>
              <Label>Last Name</Label>
              <Input
                value={formData.last_name}
                onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                required
              />
            </div>
          </div>
          <div>
            <Label>Email</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
            />
          </div>
          <div>
            <Label>Phone</Label>
            <Input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              placeholder="(876) 555-0000"
              required
            />
          </div>
          <div>
            <Label>Date of Birth</Label>
            <Input
              type="date"
              value={formData.date_of_birth}
              onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
              required
            />
          </div>
          <div>
            <Label>Password</Label>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              placeholder="Min 6 characters"
              minLength={6}
              required
            />
          </div>
          <div>
            <Label>Confirm Password</Label>
            <Input
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
              required
            />
          </div>
          <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700" disabled={loading}>
            {loading ? 'Creating Account...' : 'Create Account'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-slate-500">
            Already have an account?{' '}
            <button onClick={onSwitchToLogin} className="text-teal-600 font-medium hover:underline">
              Sign in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

// Dashboard
const Dashboard = ({ onBook }) => {
  const { token, patient } = usePortalAuth();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/portal/my-appointments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAppointments(response.data);
    } catch (error) {
      console.error('Failed to fetch appointments');
    } finally {
      setLoading(false);
    }
  };

  const cancelAppointment = async (appointmentId) => {
    if (!window.confirm('Are you sure you want to cancel this appointment?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/portal/cancel-appointment/${appointmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Appointment cancelled');
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel appointment');
    }
  };

  const upcomingAppointments = appointments.filter(a => 
    new Date(`${a.scheduled_date} ${a.scheduled_time}`) > new Date() && 
    !['cancelled', 'completed'].includes(a.status)
  );

  const pastAppointments = appointments.filter(a => 
    new Date(`${a.scheduled_date} ${a.scheduled_time}`) <= new Date() ||
    ['cancelled', 'completed'].includes(a.status)
  );

  return (
    <div className="grid md:grid-cols-3 gap-6">
      {/* Welcome Card */}
      <div className="md:col-span-3 bg-gradient-to-r from-teal-600 to-cyan-600 rounded-2xl p-6 text-white">
        <h2 className="text-2xl font-bold mb-2">Hello, {patient?.first_name}!</h2>
        <p className="opacity-90">Manage your dental appointments and book new visits.</p>
        <Button 
          className="mt-4 bg-white text-teal-600 hover:bg-slate-100"
          onClick={onBook}
        >
          <Plus className="w-4 h-4 mr-2" />
          Book New Appointment
        </Button>
      </div>

      {/* Upcoming Appointments */}
      <div className="md:col-span-2 bg-white rounded-2xl shadow p-6">
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <CalendarIcon className="w-5 h-5 text-teal-600" />
          Upcoming Appointments
        </h3>
        
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
          </div>
        ) : upcomingAppointments.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <CalendarIcon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>No upcoming appointments</p>
            <Button className="mt-4" onClick={onBook}>
              Book Now
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {upcomingAppointments.map(appt => (
              <div key={appt.id} className="border rounded-xl p-4 hover:bg-slate-50 transition-colors">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium text-lg">{appt.services?.[0]?.service_name || 'Dental Appointment'}</div>
                    <div className="text-slate-500 flex items-center gap-2 mt-1">
                      <CalendarIcon className="w-4 h-4" />
                      {new Date(appt.scheduled_date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                    </div>
                    <div className="text-slate-500 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      {appt.scheduled_time}
                    </div>
                    <div className="text-sm text-slate-400 mt-1">Dr. {appt.provider_name}</div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      {appt.status === 'confirmed' ? 'Confirmed' : 'Scheduled'}
                    </span>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="text-red-600"
                      onClick={() => cancelAppointment(appt.id)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Info */}
      <div className="bg-white rounded-2xl shadow p-6">
        <h3 className="font-semibold text-lg mb-4">Your Profile</h3>
        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-3 text-slate-600">
            <User className="w-4 h-4" />
            {patient?.first_name} {patient?.last_name}
          </div>
          <div className="flex items-center gap-3 text-slate-600">
            <Mail className="w-4 h-4" />
            {patient?.email}
          </div>
          <div className="flex items-center gap-3 text-slate-600">
            <Phone className="w-4 h-4" />
            {patient?.phone}
          </div>
        </div>

        <hr className="my-4" />

        <h4 className="font-medium text-sm text-slate-700 mb-2">Past Visits</h4>
        <div className="text-2xl font-bold text-teal-600">{pastAppointments.length}</div>
        <p className="text-xs text-slate-500">Total appointments</p>
      </div>
    </div>
  );
};

// Book Appointment
const BookAppointment = ({ onBack }) => {
  const { token } = usePortalAuth();
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots();
    }
  }, [selectedDate]);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/portal/services`);
      setServices(response.data);
    } catch (error) {
      toast.error('Failed to load services');
    }
  };

  const fetchAvailableSlots = async () => {
    setLoadingSlots(true);
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      const response = await axios.get(`${API_URL}/api/portal/available-slots?date=${dateStr}`);
      setAvailableSlots(response.data);
    } catch (error) {
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleBook = async () => {
    setLoading(true);
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      await axios.post(
        `${API_URL}/api/portal/book-appointment?service_id=${selectedService.id}&scheduled_date=${dateStr}&scheduled_time=${selectedSlot}`,
        { notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Appointment booked successfully!');
      onBack();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Booking failed');
    } finally {
      setLoading(false);
    }
  };

  // Group services by category
  const servicesByCategory = services.reduce((acc, svc) => {
    if (!acc[svc.category]) acc[svc.category] = [];
    acc[svc.category].push(svc);
    return acc;
  }, {});

  return (
    <div className="max-w-2xl mx-auto">
      {/* Progress */}
      <div className="flex items-center justify-between mb-8">
        <Button variant="ghost" onClick={onBack}>
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div className="flex gap-2">
          {[1, 2, 3].map(s => (
            <div
              key={s}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                s === step ? 'bg-teal-600 text-white' : 
                s < step ? 'bg-teal-100 text-teal-600' : 'bg-slate-200 text-slate-500'
              }`}
            >
              {s < step ? <Check className="w-4 h-4" /> : s}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-xl p-8">
        {/* Step 1: Select Service */}
        {step === 1 && (
          <>
            <h2 className="text-2xl font-bold text-slate-800 mb-6">Select Service</h2>
            <div className="space-y-6 max-h-96 overflow-y-auto">
              {Object.entries(servicesByCategory).map(([category, categoryServices]) => (
                <div key={category}>
                  <h3 className="font-medium text-slate-500 text-sm mb-2">{category}</h3>
                  <div className="grid gap-2">
                    {categoryServices.map(service => (
                      <div
                        key={service.id}
                        className={`p-4 border rounded-lg cursor-pointer transition-all ${
                          selectedService?.id === service.id 
                            ? 'border-teal-500 bg-teal-50' 
                            : 'hover:border-teal-300 hover:bg-slate-50'
                        }`}
                        onClick={() => setSelectedService(service)}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="font-medium">{service.service_name}</div>
                            <div className="text-sm text-slate-500">{service.duration_minutes} min</div>
                          </div>
                          <div className="text-teal-600 font-medium">
                            J${service.default_price_jmd?.toLocaleString()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <Button 
              className="w-full mt-6 bg-teal-600 hover:bg-teal-700" 
              disabled={!selectedService}
              onClick={() => setStep(2)}
            >
              Continue
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </>
        )}

        {/* Step 2: Select Date & Time */}
        {step === 2 && (
          <>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Select Date & Time</h2>
            <p className="text-slate-500 mb-6">{selectedService?.service_name}</p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label className="mb-2 block">Select Date</Label>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => date < new Date() || date.getDay() === 0}
                  className="rounded-md border"
                />
              </div>
              <div>
                <Label className="mb-2 block">Available Times</Label>
                {!selectedDate ? (
                  <p className="text-slate-500 text-center py-8">Select a date first</p>
                ) : loadingSlots ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
                  </div>
                ) : availableSlots.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No slots available for this date</p>
                ) : (
                  <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                    {availableSlots.map(slot => (
                      <button
                        key={slot.time}
                        className={`p-2 text-sm rounded border transition-all ${
                          selectedSlot === slot.time
                            ? 'bg-teal-600 text-white border-teal-600'
                            : 'hover:border-teal-300 hover:bg-teal-50'
                        }`}
                        onClick={() => setSelectedSlot(slot.time)}
                      >
                        {slot.time}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-4 mt-6">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                Back
              </Button>
              <Button 
                className="flex-1 bg-teal-600 hover:bg-teal-700" 
                disabled={!selectedDate || !selectedSlot}
                onClick={() => setStep(3)}
              >
                Continue
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </>
        )}

        {/* Step 3: Confirm */}
        {step === 3 && (
          <>
            <h2 className="text-2xl font-bold text-slate-800 mb-6">Confirm Booking</h2>
            
            <div className="bg-slate-50 rounded-lg p-6 mb-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-slate-500">Service</div>
                  <div className="font-medium">{selectedService?.service_name}</div>
                </div>
                <div>
                  <div className="text-slate-500">Price</div>
                  <div className="font-medium text-teal-600">
                    J${selectedService?.default_price_jmd?.toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="text-slate-500">Date</div>
                  <div className="font-medium">
                    {selectedDate?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                  </div>
                </div>
                <div>
                  <div className="text-slate-500">Time</div>
                  <div className="font-medium">{selectedSlot}</div>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <Label>Additional Notes (optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any concerns or requests..."
                rows={3}
              />
            </div>

            <div className="flex gap-4">
              <Button variant="outline" onClick={() => setStep(2)} className="flex-1">
                Back
              </Button>
              <Button 
                className="flex-1 bg-teal-600 hover:bg-teal-700" 
                onClick={handleBook}
                disabled={loading}
              >
                {loading ? 'Booking...' : 'Confirm Booking'}
                <Check className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PatientPortal;
